// Admin stock movements ledger
import React, { useEffect, useState } from "react";
import { ArrowDownUp, ArrowUp, ArrowDown, AlertTriangle, RotateCcw, Truck, Sliders } from "lucide-react";
import AdminLayout from "../../admin/AdminLayout";
import AdminAuthGuard from "../../admin/AdminAuthGuard";
import { api } from "../../api/client";

const TYPE_ICON = {
    inward: ArrowDown,
    outward: ArrowUp,
    damage: AlertTriangle,
    recall: AlertTriangle,
    transfer: ArrowDownUp,
    adjustment: Sliders,
    return_restock: RotateCcw,
};
const TYPE_TONE = {
    inward: "text-emerald-700 bg-emerald-50",
    outward: "text-rose-700 bg-rose-50",
    damage: "text-amber-700 bg-amber-50",
    recall: "text-rose-700 bg-rose-50",
    transfer: "text-blue-700 bg-blue-50",
    adjustment: "text-zinc-700 bg-zinc-50",
    return_restock: "text-violet-700 bg-violet-50",
};

const AdminMovements = () => {
    const [type, setType] = useState("all");
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        const params = { page_size: 50 };
        if (type !== "all") params.type = type;
        api.get("/admin/stock-movements", { params })
            .then((r) => setItems(r.data.items || []))
            .finally(() => setLoading(false));
    }, [type]);

    return (
        <AdminAuthGuard>
            <AdminLayout title="Stock movements" subtitle="Inventory audit trail · FIFO + recalls + damage">
                <div className="mb-4 flex flex-wrap gap-1.5" data-testid="admin-movements-filter">
                    {["all", "inward", "outward", "damage", "recall", "transfer", "return_restock"].map((t) => (
                        <button key={t} onClick={() => setType(t)} className={`rounded-full px-3 py-1.5 text-xs font-medium ${type === t ? "bg-brand-700 text-white" : "border border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50"}`} data-testid={`admin-movements-tab-${t}`}>{t.replace("_", " ")}</button>
                    ))}
                </div>

                {loading ? <div className="h-48 skeleton rounded-2xl" /> : items.length === 0 ? (
                    <div className="rounded-2xl border border-dashed border-zinc-200 bg-white p-12 text-center text-sm text-slate-500" data-testid="admin-movements-empty">
                        <ArrowDownUp className="mx-auto h-8 w-8 text-slate-300" />
                        <p className="mt-2">No movements in this view.</p>
                    </div>
                ) : (
                    <div className="overflow-hidden rounded-2xl border border-zinc-100 bg-white shadow-sm">
                        <table className="w-full text-sm" data-testid="admin-movements-table">
                            <thead className="border-b border-zinc-100 bg-zinc-50 text-left text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
                                <tr>
                                    <th className="px-4 py-3">Type</th>
                                    <th className="px-4 py-3">Product</th>
                                    <th className="px-4 py-3">Qty Δ</th>
                                    <th className="px-4 py-3">Reason</th>
                                    <th className="px-4 py-3">Linked to</th>
                                    <th className="px-4 py-3">At</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-zinc-50">
                                {items.map((m) => {
                                    const Icon = TYPE_ICON[m.type] || ArrowDownUp;
                                    const sign = m.qty_delta > 0 ? "+" : "";
                                    return (
                                        <tr key={m.id} className="hover:bg-zinc-50" data-testid={`admin-movements-row-${m.id}`}>
                                            <td className="px-4 py-3">
                                                <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${TYPE_TONE[m.type] || "bg-zinc-100"}`}>
                                                    <Icon className="h-3 w-3" />
                                                    {m.type.replace("_", " ")}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-xs">{m.product_name || m.product_id?.slice(0, 8)}</td>
                                            <td className={`px-4 py-3 text-sm font-semibold ${m.qty_delta > 0 ? "text-emerald-700" : m.qty_delta < 0 ? "text-rose-700" : "text-zinc-500"}`}>{sign}{m.qty_delta}</td>
                                            <td className="px-4 py-3 text-xs text-slate-600">{m.reason || "—"}</td>
                                            <td className="px-4 py-3 text-[11px] text-slate-500">
                                                {m.related_order_id && <span title={m.related_order_id}>Order: <code>{m.related_order_id.slice(0, 8)}</code></span>}
                                                {m.related_po_id && <span title={m.related_po_id}>PO: <code>{m.related_po_id.slice(0, 8)}</code></span>}
                                                {!m.related_order_id && !m.related_po_id && <span className="text-zinc-400">—</span>}
                                            </td>
                                            <td className="px-4 py-3 text-[11px] text-slate-500">{new Date(m.created_at).toLocaleString()}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </AdminLayout>
        </AdminAuthGuard>
    );
};

export default AdminMovements;
